import { VRCertificateTypes } from "@constants/certificates";
import type { VesselRegisterFormData } from "@features/vessel-register/schema/vesselRegisterFormSchema";
import cloneDeep from "lodash/cloneDeep";
import lodashSet from "lodash/set";
import type { FieldPath, FieldPathValue } from "react-hook-form";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { CertificateFields, FORM_FIELDS, generateLegacyCertificateField } from "../utils";

interface VesselRegisterFormMethods {
  setField<K extends FieldPath<VesselRegisterFormData>>(
    key: K,
    val: FieldPathValue<VesselRegisterFormData, K>,
  ): void;
  setAll(data: Partial<VesselRegisterFormData>): void;
  resetForm(): void;
}

export interface VesselRegisterFormState
  extends VesselRegisterFormData,
    VesselRegisterFormMethods {}

const certificateDefaults = Object.values(VRCertificateTypes).reduce<
  NonNullable<VesselRegisterFormData[typeof FORM_FIELDS.CERTIFICATES]>
>((acc, cert) => {
  acc[cert] = {
    [CertificateFields.URL]: "",
    [CertificateFields.DOCUMENT_NAME]: "",
    [CertificateFields.CLASS_NAME]: "",
    [CertificateFields.EXPIRY_DATE]: null,
    [CertificateFields.PERMANENT]: false,
  };
  return acc;
}, {});

export const defaultValues: VesselRegisterFormData = {
  // Vessel Information
  [FORM_FIELDS.VESSEL_NAME]: "",
  [FORM_FIELDS.IMO]: undefined,
  [FORM_FIELDS.OFFICIAL_NUMBER]: "",
  [FORM_FIELDS.NO_IMO_NUMBER]: false,
  [FORM_FIELDS.CALL_SIGN]: "",
  [FORM_FIELDS.PORT_OF_REGISTRATION]: "",
  [FORM_FIELDS.FLAG]: "",
  [FORM_FIELDS.YEAR_BUILT]: undefined,
  [FORM_FIELDS.DATE_OF_DELIVERY]: "",
  // Vessel Particulars
  [FORM_FIELDS.VESSEL_TYPE_CODE]: "",
  [FORM_FIELDS.BUNKER_REGISTERED]: false,
  [FORM_FIELDS.DIPLOMATIC_VESSEL]: false,
  [FORM_FIELDS.GROSS_TONNAGE]: undefined,
  [FORM_FIELDS.NET_TONNAGE]: undefined,
  [FORM_FIELDS.DEADWEIGHT_TONNAGE]: undefined,
  [FORM_FIELDS.LENGTH_OVERALL]: undefined,
  [FORM_FIELDS.DRAFT_MAX]: undefined,
  [FORM_FIELDS.VESSEL_CLASSIFICATION_SOCIETY]: "",
  [FORM_FIELDS.OTHER_VESSEL_CLASSIFICATION_SOCIETY]: "",
  [FORM_FIELDS.BEAM]: undefined,
  // Vessel Owner & Registration
  [FORM_FIELDS.SHIP_OWNER]: "",
  [FORM_FIELDS.SHIP_CHARTERER]: "",
  [FORM_FIELDS.SHIPS_MANAGEMENT]: "",
  [FORM_FIELDS.SHIPS_REGISTERED_OWNER]: "",
  [FORM_FIELDS.PI_CLUB]: "",
  [FORM_FIELDS.MLC_CLUB]: "",
  [FORM_FIELDS.PI_CLUB_OTHER]: "",
  [FORM_FIELDS.MLC_CLUB_OTHER]: "",
  [FORM_FIELDS.REGISTRATION_REMARKS]: "",
  // Additional documents
  [FORM_FIELDS.ADDITIONAL_DOCUMENTS]: [],
  //Bunker Barge
  [FORM_FIELDS.NATURE_OF_SERVICE]: "BUNKERING",
  [FORM_FIELDS.BUNKERING_COMPANY]: "",
  [FORM_FIELDS.NAVIGATION_LICENSE]: [],
  [FORM_FIELDS.NAVIGATION_LICENSE_FROM_DATE]: "",
  [FORM_FIELDS.NAVIGATION_LICENSE_TO_DATE]: "",
  [FORM_FIELDS.CHARTER_PARTY_AGREEMENT]: [],
  [FORM_FIELDS.REGISTERED_OWNER]: "",
  [FORM_FIELDS.CHARTER_START_DATE]: "",
  [FORM_FIELDS.CHARTER_END_DATE]: "",
  [FORM_FIELDS.OIL_SPILL_COMPANY]: [],
  [FORM_FIELDS.OIL_COMPANY]: "",
  [FORM_FIELDS.OIL_SPILL_COMPANY_FROM_DATE]: "",
  [FORM_FIELDS.OIL_SPILL_COMPANY_TO_DATE]: "",
  [FORM_FIELDS.SIRE_REPORT]: [],
  [FORM_FIELDS.SIRE_REPORT_FROM_DATE]: "",
  [FORM_FIELDS.SIRE_REPORT_TO_DATE]: "",
  [FORM_FIELDS.HOSE_TEST_CERTIFICATE]: [],
  [FORM_FIELDS.COMPANY_NAME_HOSE_TEST_CERTIFICATE]: "",
  [FORM_FIELDS.HOSE_TEST_CERTIFICATE_EXPIRY_DATE]: "",
  [FORM_FIELDS.SHIP_TO_SHIP_PLAN]: [],
  [FORM_FIELDS.SHIP_TO_SHIP_PLAN_FROM_DATE]: "",
  [FORM_FIELDS.SHIP_TO_SHIP_PLAN_TO_DATE]: "",
  [FORM_FIELDS.EFFECTIVE_START_DATE]: "",
  [FORM_FIELDS.EFFECTIVE_END_DATE]: "",
  [FORM_FIELDS.REMARKS]: "",
  [FORM_FIELDS.BUNKER_BARGE_ADDITIONAL_DOCUMENTS]: [],
  // Vessel Certificates
  [FORM_FIELDS.CERTIFICATES]: certificateDefaults,
};

const NUMERIC_FIELDS = [
  FORM_FIELDS.GROSS_TONNAGE,
  FORM_FIELDS.NET_TONNAGE,
  FORM_FIELDS.DEADWEIGHT_TONNAGE,
  FORM_FIELDS.LENGTH_OVERALL,
  FORM_FIELDS.DRAFT_MAX,
  FORM_FIELDS.BEAM,
];

export function transformNumberToString(data: Record<string, unknown>) {
  return Object.fromEntries(
    Object.entries(data).map(([key, value]) =>
      NUMERIC_FIELDS.includes(key as (typeof NUMERIC_FIELDS)[number])
        ? [key, value !== undefined && value !== null ? value.toString() : ""]
        : [key, value],
    ),
  );
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

const normalizeCertificateExpiryDate = (value: unknown): Date | null | undefined => {
  if (value instanceof Date) return value;
  if (typeof value !== "string") return undefined;

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const mergeVesselRegisterFormData = (
  base: VesselRegisterFormState,
  data: Partial<VesselRegisterFormData>,
): VesselRegisterFormState => ({
  ...base,
  ...data,
  [FORM_FIELDS.CERTIFICATES]: {
    ...(base[FORM_FIELDS.CERTIFICATES] ?? {}),
    ...(data[FORM_FIELDS.CERTIFICATES] ?? {}),
  },
});

export const migrateLegacyCertificateFields = (
  persisted: unknown,
): Partial<VesselRegisterFormData> => {
  if (!isRecord(persisted)) return {};

  const migrated: Record<string, unknown> = { ...persisted };
  const persistedCertificates = persisted[FORM_FIELDS.CERTIFICATES];
  const persistedCertificateValues = isRecord(persistedCertificates) ? persistedCertificates : {};
  const certificates = {
    ...cloneDeep(certificateDefaults),
    ...persistedCertificateValues,
  } as NonNullable<VesselRegisterFormData[typeof FORM_FIELDS.CERTIFICATES]>;

  Object.values(VRCertificateTypes).forEach((cert) => {
    const existing = isRecord(certificates[cert]) ? certificates[cert] : {};
    const legacyUrl = persisted[generateLegacyCertificateField.url(cert)];
    const legacyDocumentName = persisted[generateLegacyCertificateField.documentName(cert)];
    const legacyClassName = persisted[generateLegacyCertificateField.className(cert)];
    const legacyExpiryDate = persisted[generateLegacyCertificateField.expiryDate(cert)];
    const legacyPermanent = persisted[generateLegacyCertificateField.permanent(cert)];
    const normalizedLegacyExpiryDate = normalizeCertificateExpiryDate(legacyExpiryDate);

    certificates[cert] = {
      ...existing,
      ...(typeof legacyUrl === "string" ? { [CertificateFields.URL]: legacyUrl } : {}),
      ...(typeof legacyDocumentName === "string"
        ? { [CertificateFields.DOCUMENT_NAME]: legacyDocumentName }
        : {}),
      ...(typeof legacyClassName === "string"
        ? { [CertificateFields.CLASS_NAME]: legacyClassName }
        : {}),
      ...(normalizedLegacyExpiryDate !== undefined
        ? { [CertificateFields.EXPIRY_DATE]: normalizedLegacyExpiryDate }
        : {}),
      ...(typeof legacyPermanent === "boolean"
        ? { [CertificateFields.PERMANENT]: legacyPermanent }
        : {}),
    };

    delete migrated[generateLegacyCertificateField.url(cert)];
    delete migrated[generateLegacyCertificateField.documentName(cert)];
    delete migrated[generateLegacyCertificateField.className(cert)];
    delete migrated[generateLegacyCertificateField.expiryDate(cert)];
    delete migrated[generateLegacyCertificateField.permanent(cert)];
  });

  return {
    ...migrated,
    [FORM_FIELDS.CERTIFICATES]: certificates,
  } as Partial<VesselRegisterFormData>;
};

export const useVesselRegisterFormStore = create<VesselRegisterFormState>()(
  persist(
    (set: (fn: (state: VesselRegisterFormState) => VesselRegisterFormState) => void) => ({
      ...defaultValues,
      setField: (key, val) =>
        set((state) => {
          const newState = cloneDeep(state);
          lodashSet(newState, key, val);
          return newState;
        }),
      setAll: (data) => set((state) => mergeVesselRegisterFormData(state, data)),
      resetForm: () => set((state) => ({ ...state, ...cloneDeep(defaultValues) })),
    }),
    {
      name: "vessel-registration-form",
      version: 1,
      migrate: (persisted) => migrateLegacyCertificateFields(persisted),
    },
  ),
);
